import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import useFieldEditor from '../hooks/useFieldEditor';
import EditorCanvas from '../components/editor/EditorCanvas';
import EditorFloatingToolbar from '../components/editor/EditorFloatingToolbar';
import EditorFieldConfig from '../components/editor/EditorFieldConfig';
import EditorFieldsList from '../components/editor/EditorFieldsList';
// import FieldGroupsPanel from '../components/editor/FieldGroupsPanel'; // Temporarily disabled
import EditorLinkModal from '../components/editor/EditorLinkModal';
import api, { getApiBaseUrl } from '../services/api';
import { isTextFieldType } from '../utils/fieldEditorUtils';
import { useModal } from '../context/ModalContext';
import fieldConfigService from '../services/fieldConfigService';
import { buildFieldQualityAdvisory } from '../utils/fieldQualityNotify';

import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default function FieldEditorPage() {
  const NUDGE_STEP = 0.2;
  const { formId } = useParams();
  const navigate = useNavigate();
  const { showAlert, showConfirm, showPrompt } = useModal();

  // PDF state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [addFieldMode, setAddFieldMode] = useState(null);
  const [toast, setToast] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const [formLevelSettings, setFormLevelSettings] = useState({
    primaryFieldName: '',
    markStyle: 'tick',
    radioStyle: 'dot',
    showRadioBorder: true,
    dateFormat: 'DD/MM/YYYY',
    conditionalFieldsEnabled: false,
    limitToBox: false,
    globalFontFamily: 'Times New Roman',
    globalFontSize: '12'
  });

  // Refs
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const containerRef = useRef(null);
  const editorRootRef = useRef(null);
  const viewportRef = useRef(null);
  const defaultsAppliedRef = useRef(false);
  const nudgeIntervalRef = useRef(null);
  const activeNudgeKeyRef = useRef(null);
  const moreActionsRef = useRef(null);

  useEffect(() => {
    if (!moreActionsOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!moreActionsRef.current?.contains(event.target)) setMoreActionsOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
    };
  }, [moreActionsOpen]);

  // Field editor hook
  const editor = useFieldEditor(formId);

  // Show toast notification
  const showToast = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2000);
  }, []);

  // Load PDF document
  const loadPdf = useCallback(async () => {
    if (!formId) return;
    try {
      const API_URL = getApiBaseUrl();
      const loadingTask = pdfjsLib.getDocument({
        url: `${API_URL}/forms/${formId}/template`,
        withCredentials: true
      });
      const doc = await loadingTask.promise;
      setPdfDoc(doc);
      setTotalPages(doc.numPages);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  }, [formId]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      await editor.loadForm();
      await loadPdf();
    };
    init();

    // Cleanup: destroy pdf.js document on unmount to prevent state leaks during SPA navigation
    return () => {
      setPdfDoc(prev => {
        if (prev) {
          prev.destroy();
        }
        return null;
      });
    };
  }, [formId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync form-level settings from configuration and ensure defaults
  useEffect(() => {
    if (editor.configuration && !defaultsAppliedRef.current) {
      // Treat 'auto' markStyle as 'tick' since 'auto' causes checkboxes to not render
      const effectiveMarkStyle = (!editor.configuration.markStyle || editor.configuration.markStyle === 'auto')
        ? 'tick'
        : editor.configuration.markStyle;
      
      const newSettings = {
        primaryFieldName: editor.configuration.primaryFieldName || '',
        markStyle: effectiveMarkStyle,
        radioStyle: editor.configuration.radioStyle || 'dot',
        showRadioBorder: editor.configuration.showRadioBorder !== false,
        dateFormat: editor.configuration.dateFormat || 'DD/MM/YYYY',
        conditionalFieldsEnabled: editor.configuration.conditionalFieldsEnabled !== false,
        limitToBox: editor.configuration.limitToBox === true,
        globalFontFamily: editor.configuration.globalFontFamily || 'Times New Roman',
        globalFontSize: editor.configuration.globalFontSize || '12'
      };
      
      setFormLevelSettings(newSettings);
      
      // If configuration is missing default values or has 'auto' markStyle, save them (only once)
      const needsUpdate =
        !editor.configuration.markStyle ||
        editor.configuration.markStyle === 'auto' ||
        !editor.configuration.radioStyle ||
        !editor.configuration.dateFormat ||
        !editor.configuration.globalFontFamily ||
        !editor.configuration.globalFontSize;
      
      if (needsUpdate) {
        defaultsAppliedRef.current = true; // Mark as applied to prevent re-triggering
        const updatedConfig = {
          ...editor.configuration,
          markStyle: newSettings.markStyle,
          radioStyle: newSettings.radioStyle,
          dateFormat: newSettings.dateFormat,
          globalFontFamily: newSettings.globalFontFamily,
          globalFontSize: newSettings.globalFontSize
        };
        editor.setConfiguration(updatedConfig);
        editor.saveConfigToServer(updatedConfig).then(() => {
          console.log('Default settings applied:', updatedConfig);
        }).catch(err => console.error('Failed to save default settings:', err));
      } else {
        // Configuration already has all defaults
        defaultsAppliedRef.current = true;
      }
    } else if (editor.configuration && defaultsAppliedRef.current) {
      // Just sync the settings without saving
      setFormLevelSettings({
        primaryFieldName: editor.configuration.primaryFieldName || '',
        markStyle: (!editor.configuration.markStyle || editor.configuration.markStyle === 'auto') ? 'tick' : editor.configuration.markStyle,
        radioStyle: editor.configuration.radioStyle || 'dot',
        showRadioBorder: editor.configuration.showRadioBorder !== false,
        dateFormat: editor.configuration.dateFormat || 'DD/MM/YYYY',
        conditionalFieldsEnabled: editor.configuration.conditionalFieldsEnabled !== false,
        limitToBox: editor.configuration.limitToBox === true,
        globalFontFamily: editor.configuration.globalFontFamily || 'Times New Roman',
        globalFontSize: editor.configuration.globalFontSize || '12'
      });
    }
  }, [editor.configuration]);

  const focusEditorCanvas = useCallback(() => {
    const root = editorRootRef.current;
    if (!root) return;

    const activeElement = document.activeElement;
    const activeTag = activeElement?.tagName;
    const isTypingTarget = ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeTag) || activeElement?.isContentEditable;

    if (isTypingTarget && activeElement?.closest('.editor-right-panel, .editor-left-panel, .field-toolbar')) {
      return;
    }

    if (document.activeElement !== root) {
      root.focus({ preventScroll: true });
    }
  }, [editor.selectedFieldIndex, editor.selectedFieldIndices]);

  // ---- KEYBOARD SHORTCUTS ----
  useEffect(() => {

    const handleKeyDown = async (e) => {
      const activeElement = document.activeElement;
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement?.tagName) || activeElement?.isContentEditable;
      const nudgeMap = {
        ArrowUp: { x: 0, y: NUDGE_STEP },
        ArrowDown: { x: 0, y: -NUDGE_STEP },
        ArrowLeft: { x: -NUDGE_STEP, y: 0 },
        ArrowRight: { x: NUDGE_STEP, y: 0 }
      };

      const stopNudging = () => {
        if (nudgeIntervalRef.current) {
          clearInterval(nudgeIntervalRef.current);
          nudgeIntervalRef.current = null;
        }
        activeNudgeKeyRef.current = null;
      };

      const nudgeDirection = nudgeMap[e.key];
      if (nudgeDirection) {
      }

      if (nudgeDirection && !isInput && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const hasSelection = editor.selectedFieldIndices.size > 0 || editor.selectedFieldIndex !== null;
        if (!hasSelection) return;

        e.preventDefault();

        if (activeNudgeKeyRef.current === e.key && e.repeat) {
          return;
        }

        stopNudging();
        const moved = editor.nudgeSelectedFields(nudgeDirection.x, nudgeDirection.y);
        activeNudgeKeyRef.current = e.key;
        nudgeIntervalRef.current = setInterval(() => {
          const repeatMoved = editor.nudgeSelectedFields(nudgeDirection.x, nudgeDirection.y);
        }, 50);
        return;
      }

      if (e.key === 'Tab' && !isInput && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        editor.selectNextField(e.shiftKey ? -1 : 1);
        return;
      }

      // Ctrl+Z - Undo
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey && !isInput) {
        e.preventDefault();
        editor.undo().then(ok => showToast(ok ? 'Undo' : 'Nothing to undo'));
        return;
      }
      // Ctrl+Shift+Z or Ctrl+Y - Redo
      if (((e.ctrlKey && e.shiftKey && e.key === 'Z') || (e.ctrlKey && e.key === 'y')) && !isInput) {
        e.preventDefault();
        editor.redo().then(ok => showToast(ok ? 'Redo' : 'Nothing to redo'));
        return;
      }
      // Delete/Backspace/D - Delete selected
      if ((e.key === 'Delete' || e.key === 'Backspace' || e.key === 'd' || e.key === 'D') && !isInput) {
        if (editor.selectedFieldIndices.size > 0) {
          e.preventDefault();
          const ok = await showConfirm(`Delete ${editor.selectedFieldIndices.size} selected field(s)?`, { title: 'Delete Fields', danger: true });
          if (ok) {
            await editor.deleteSelectedFields();
            showToast('Fields deleted');
          }
        } else if (editor.selectedFieldIndex !== null) {
          e.preventDefault();
          const field = editor.pdfFields[editor.selectedFieldIndex];
          if (field) {
            const ok = await showConfirm(`Delete field "${field.name}"?`, { title: 'Delete Field', danger: true });
            if (ok) {
              await editor.deleteField(editor.selectedFieldIndex);
              showToast('Field deleted');
            }
          }
        }
      }
      // Escape - Clear selection / exit add mode
      if (e.key === 'Escape') {
        if (moreActionsOpen) {
          e.preventDefault();
          setMoreActionsOpen(false);
          return;
        }
        if (addFieldMode) {
          setAddFieldMode(null);
        } else {
          editor.clearSelection();
        }
      }
      // Ctrl+A - Select all
      if (e.ctrlKey && e.key === 'a' && !isInput) {
        e.preventDefault();
        editor.selectAllFields();
      }
      // Ctrl+C - Copy
      if (e.ctrlKey && e.key === 'c' && !isInput) {
        if (editor.selectedFieldIndices.size > 0 || editor.selectedFieldIndex !== null) {
          e.preventDefault();
          const count = editor.copySelectedFields();
          showToast(`${count} field(s) copied`);
        }
      }
      // G - assign selected radio group
      if ((e.key === 'g' || e.key === 'G') && !e.ctrlKey && !e.metaKey && !e.altKey && !isInput) {
        if (editor.selectedFieldIndices.size >= 2) {
          e.preventDefault();
          const autoGroupName = editor.generateUniqueRadioGroupName();
          const ok = await editor.assignSelectedFieldsToRadioGroup(autoGroupName);
          if (ok) {
            showToast(`Assigned ${editor.selectedFieldIndices.size} fields to radio group "${autoGroupName}"`);
          }
        }
      }
      // Ctrl+V - Paste
      if (e.ctrlKey && e.key === 'v' && !isInput) {
        if (editor.clipboard.length > 0) {
          e.preventDefault();
          const mousePos = containerRef.current?._getLastMousePos?.() || { x: 100, y: 100, page: 1 };
          const viewportInfo = Array.isArray(viewportRef.current)
            ? viewportRef.current.find(v => v.pageNum === mousePos.page)
            : viewportRef.current;
          editor.pasteFields(mousePos.x, mousePos.y, mousePos.page || 1, pdfDoc, viewportInfo?.scale || 1)
            .then(count => showToast(`${count} field(s) pasted`));
        }
      }
    };

    // One listener only. The handler used to be attached to both the editor root and
    // document; the root is inside the document, so a single Ctrl+V bubbled through
    // both and pasted twice (and Ctrl+Z undid two steps). preventDefault does not stop
    // propagation, so the second call was never suppressed.
    document.addEventListener('keydown', handleKeyDown);
    const handleKeyUp = (e) => {
      if (activeNudgeKeyRef.current === e.key && nudgeIntervalRef.current) {
        clearInterval(nudgeIntervalRef.current);
        nudgeIntervalRef.current = null;
        activeNudgeKeyRef.current = null;
      }
    };

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (nudgeIntervalRef.current) {
        clearInterval(nudgeIntervalRef.current);
        nudgeIntervalRef.current = null;
      }
      activeNudgeKeyRef.current = null;
    };
  }, [editor, addFieldMode, moreActionsOpen, pdfDoc, showToast]);

  useEffect(() => {
    if (editor.selectedFieldIndex !== null || editor.selectedFieldIndices.size > 0) {
      focusEditorCanvas();
    }
  }, [editor.selectedFieldIndex, editor.selectedFieldIndices, focusEditorCanvas]);

  // ---- ZOOM ----
  const adjustZoom = (delta) => {
    setZoomLevel(prev => Math.max(0.5, Math.min(3.0, prev + delta)));
  };
  const resetZoom = () => setZoomLevel(1.0);

  // ---- ADD FIELD MODE ----
  const toggleAddFieldMode = (fieldType) => {
    if (addFieldMode === fieldType) {
      setAddFieldMode(null);
    } else {
      setAddFieldMode(fieldType);
    }
  };

  // ---- CREATE CUSTOM FIELD ----
  const handleCreateCustomField = async (x, y, width, height, fieldType, pageNum = 1) => {
    try {
      const viewportInfo = Array.isArray(viewportRef.current)
        ? viewportRef.current.find(v => v.pageNum === pageNum)
        : viewportRef.current;
      await editor.createCustomField(x, y, width, height, fieldType, pageNum, pdfDoc, viewportInfo?.scale || 1);
      setAddFieldMode(null);
      showToast(`Created ${fieldType} field`);
    } catch (error) {
      await showAlert('Failed to create field: ' + error.message, 'Error');
    }
  };

  // ---- SAVE FIELD FROM CONFIG PANEL ----
  const handleSaveFieldFromPanel = async (fieldData) => {
    try {
      await editor.saveFieldConfiguration(fieldData);
      showToast('Field saved');
    } catch (error) {
      await showAlert('Failed to save: ' + error.message, 'Error');
    }
  };

  // ---- SAVE FIELD FROM TOOLBAR ----
  const handleSaveFieldFromToolbar = async (fieldId, changes) => {
    try {
      await editor.saveFieldProperties(fieldId, changes);
    } catch (error) {
      console.error('Failed to save from toolbar:', error);
    }
  };

  // ---- AUTO-CONFIGURE ----
  const handleAutoConfigure = async () => {
    const ok = await showConfirm('Replace ALL fields with a fresh AI result? This removes existing positions, settings and custom fields.', { title: 'Reset all fields', danger: true });
    if (!ok) return;
    try {
      await editor.autoConfigureForm({ replaceExisting: true });
      await editor.loadForm();
      showToast('All fields rebuilt');
    } catch (error) {
      await showAlert('Auto-configure failed: ' + error.message, 'Error');
    }
  };

  // ---- RE-ANALYZE ----
  const handleReAnalyze = async () => {
    const ok = await showConfirm('AI will add missing fields. Existing fields, positions and settings will be kept.', { title: 'AI Re-detect' });
    if (!ok) return;
    try {
      await editor.saveAllConfiguration(formLevelSettings);
      const result = await editor.reAnalyzeFields();
      let msg = '';
      if (result.updatedCount !== undefined) msg += `Updated: ${result.updatedCount}\n`;
      if (result.addedCount !== undefined) msg += `Added: ${result.addedCount}\n`;
      if (result.totalFields !== undefined) msg += `Total: ${result.totalFields}`;
      await showAlert(msg || result.message || 'Re-analysis complete', 'Re-Analysis Complete');
      await editor.loadForm();
      await loadPdf();
    } catch (error) {
      await showAlert('Re-analyze failed: ' + error.message, 'Error');
    }
  };

  // ---- SAVE ALL ----
  const handleSaveAll = async () => {
    // Blocked before the confirm dialog: there is no point asking whether to save
    // something that will not be saved.
    //
    // The field is already marked required in the sidebar, but that message sits
    // below the fold and Save All is in the top toolbar, so a form could be saved
    // without one and the gap only surfaced later — at publish time, or as a
    // submitted PDF named "...-Unknown.pdf" that staff could not identify.
    if (!formLevelSettings.primaryFieldName) {
      await showAlert(
        primaryFieldOptions.length === 0
          ? 'This form has no Primary Field set, and there is no text field to use as one. ' +
            'Add a text field first, then choose it under Form Settings -> Primary Field.'
          : 'Please choose a Primary Field under Form Settings before saving. ' +
            'It names the submitted PDF and identifies the patient in My Submissions.',
        'Primary Field Required'
      );
      return;
    }

    const ok = await showConfirm('Save all field configurations?', { title: 'Save All' });
    if (!ok) return;
    try {
      await editor.saveAllConfiguration(formLevelSettings);
      showToast('Configuration saved');

      // Advisory only: score field boxes after an explicit Save All. Silent
      // autosaves must not show this UI. A quality failure must not undo save.
      try {
        const report = await fieldConfigService.getFieldQuality(formId);
        const labelByName = new Map(
          (editor.configuration?.fields || []).map((f) => [
            f.pdfFieldName,
            f.displayLabel || f.pdfFieldName
          ])
        );
        const advisory = buildFieldQualityAdvisory(report, { labelByName });
        if (advisory) {
          await showAlert(advisory.message, 'Field boxes to review');
        }
      } catch (qualityError) {
        console.warn('[FieldQuality] Save All quality check failed:', qualityError);
      }
    } catch (error) {
      await showAlert('Failed to save: ' + error.message, 'Error');
    }
  };

  // ---- RESET ----
  const handleReset = async () => {
    const ok = await showConfirm('Reset all configurations? This will reload from server.', { title: 'Reset', danger: true });
    if (!ok) return;
    await editor.loadForm();
    await loadPdf();
    showToast('Configuration reset');
  };

  // ---- RENAME ----
  const handleRename = async () => {
    const newName = await showPrompt('Enter new name:', editor.configuration?.formName || '', { title: 'Rename Form' });
    if (!newName || !newName.trim()) return;
    try {
      await editor.renameForm(newName.trim());
      showToast('Form renamed');
      await editor.loadForm();
    } catch (error) {
      await showAlert('Rename failed: ' + error.message, 'Error');
    }
  };

  // ---- TOGGLE TYPE ----
  const handleToggleType = async () => {
    try {
      const formInfo = (await api.get(`/forms/${formId}`)).data;
      const currentType = formInfo.isInternal ? 'Internal' : 'External';
      const newType = formInfo.isInternal ? 'External' : 'Internal';
      const ok = await showConfirm(`Change from ${currentType} to ${newType}?`, { title: 'Toggle Form Type' });
      if (!ok) return;
      const result = await editor.toggleFormType();
      showToast(`Form type changed to ${result}`);
    } catch (error) {
      await showAlert('Failed: ' + error.message, 'Error');
    }
  };

  // ---- DELETE TEMPLATE ----
  const handleDeleteTemplate = async () => {
    const ok = await showConfirm('Delete this template? This cannot be undone.', { title: 'Delete Template', danger: true });
    if (!ok) return;
    try {
      await editor.deleteTemplate();
      navigate('/admin/templates');
    } catch (error) {
      await showAlert('Delete failed: ' + error.message, 'Error');
    }
  };

  // ---- FORM LEVEL SETTINGS ----
  const handleFormSettingChange = async (key, value) => {
    const newSettings = { ...formLevelSettings, [key]: value };
    setFormLevelSettings(newSettings);
    // Auto-save form-level settings
    if (editor.configuration) {
      let newConfig = { ...editor.configuration, [key]: value };

      if (key === 'primaryFieldName') {
        // The primary field names the submitted PDF, so it must be answered. Only that
        // field is touched: an earlier version rewrote `required` on every field here
        // (`=== value ? true : false`), which silently cleared every other required
        // flag each time the primary field was changed in Settings.
        newConfig = {
          ...newConfig,
          fields: (newConfig.fields || []).map(field => (
            field.pdfFieldName === value ? { ...field, required: true } : field
          ))
        };
      }

      try {
        editor.setConfiguration(newConfig);
        await editor.saveConfigToServer(newConfig);
        showToast(`${key} updated`);
      } catch (error) {
        console.error('Failed to save setting:', error);
      }
    }
  };

  // Apply global font to all fields
  const handleApplyGlobalFont = async () => {
    if (!formLevelSettings.globalFontFamily && !formLevelSettings.globalFontSize) {
      showToast('Please select a font or size first');
      return;
    }

    // Count only visible fields
    const visibleFieldsCount = editor.configuration.fields.filter(f => f.isVisible !== false).length;

    const fontInfo = [];
    if (formLevelSettings.globalFontFamily) fontInfo.push(`font "${formLevelSettings.globalFontFamily}"`);
    if (formLevelSettings.globalFontSize) fontInfo.push(`size ${formLevelSettings.globalFontSize}`);

    const ok = await showConfirm(
      `Apply ${fontInfo.join(' and ')} to all ${visibleFieldsCount} fields?`,
      { title: 'Apply Global Font Settings' }
    );
    
    if (!ok) return;

    try {
      // Update all fields in configuration with the global font settings
      const updatedConfig = {
        ...editor.configuration,
        fields: editor.configuration.fields.map(field => {
          const updates = { ...field };
          if (formLevelSettings.globalFontFamily) {
            updates.fontFamily = formLevelSettings.globalFontFamily;
          }
          if (formLevelSettings.globalFontSize) {
            updates.fontSize = parseInt(formLevelSettings.globalFontSize);
          }
          return updates;
        })
      };

      // Save the updated configuration
      editor.setConfiguration(updatedConfig);
      await editor.saveConfigToServer(updatedConfig);
      
      showToast(`Global font settings applied to all ${visibleFieldsCount} fields`);
    } catch (error) {
      console.error('Failed to apply global font:', error);
      showToast('Failed to apply global font');
    }
  };

  // Primary field dropdown options
  const primaryFieldOptions = [];
  if (editor.configuration?.fields) {
    const addedNames = new Set();
    editor.configuration.fields.forEach(f => {
      if (f.displayType === 'text' && f.isVisible !== false && !addedNames.has(f.pdfFieldName)) {
        primaryFieldOptions.push({ value: f.pdfFieldName, label: f.pdfFieldName });
        addedNames.add(f.pdfFieldName);
      }
    });
    editor.pdfFields.forEach(pf => {
      if (!addedNames.has(pf.name) && isTextFieldType(pf)) {
        primaryFieldOptions.push({ value: pf.name, label: pf.name });
        addedNames.add(pf.name);
      }
    });
    primaryFieldOptions.sort((a, b) => a.label.localeCompare(b.label));
  }

  // Component items for left panel
  const componentTypes = [
    { type: 'text', label: 'Text Field', icon: 'T' },
    { type: 'number', label: 'Number', icon: '#' },
    { type: 'checkbox', label: 'Checkbox', icon: 'V' },
    { type: 'radio', label: 'Radio', icon: 'O' },
    { type: 'date', label: 'Date', icon: 'D' },
    { type: 'signature', label: 'Signature', icon: 'S' }
  ];

  // Loading state
  if (editor.loading && !editor.configuration) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" />
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="field-editor-page"
      ref={editorRootRef}
      tabIndex={0}
      onMouseDown={(event) => {
        if (!event.target.closest('.editor-toolbar')) focusEditorCanvas();
      }}
      style={{ outline: 'none' }}
    >
      {/* TOP TOOLBAR */}
      <div className="editor-toolbar">
        <div className="toolbar-left">
          <button className="editor-tool-button editor-tool-button--back" onClick={() => navigate('/admin/templates')} title="Back to templates">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
            Templates
          </button>
          <span className="editor-title" title={editor.configuration?.formName || 'Field Editor'}>
            {editor.configuration?.formName || 'Field Editor'}
          </span>
        </div>
        <div className="toolbar-center">
          <div className="editor-tool-group" role="group" aria-label="Edit history">
            <button className="editor-tool-button editor-tool-button--icon" onClick={() => editor.undo().then(ok => showToast(ok ? 'Undo' : 'Nothing to undo'))} title="Undo (Ctrl+Z)" aria-label="Undo">
              <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
            </button>
            <button className="editor-tool-button editor-tool-button--icon" onClick={() => editor.redo().then(ok => showToast(ok ? 'Redo' : 'Nothing to redo'))} title="Redo (Ctrl+Shift+Z)" aria-label="Redo">
              <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
            </button>
          </div>
          <div className="editor-tool-group" role="group" aria-label="PDF zoom">
            <button className="editor-tool-button editor-tool-button--icon" onClick={() => adjustZoom(-0.1)} title="Zoom out" aria-label="Zoom out">−</button>
            <button className="editor-tool-button editor-tool-button--zoom" onClick={resetZoom} title="Reset zoom to 100%" aria-label={`Reset zoom, currently ${Math.round(zoomLevel * 100)} percent`}>{Math.round(zoomLevel * 100)}%</button>
            <button className="editor-tool-button editor-tool-button--icon" onClick={() => adjustZoom(0.1)} title="Zoom in" aria-label="Zoom in">+</button>
          </div>
        </div>
        <div className="toolbar-right">
          <button className="editor-tool-button editor-tool-button--subtle" onClick={handleReAnalyze}
                  disabled={editor.loading || editor.aiProgress?.show}
                  title="Add missing fields with AI while preserving your current fields and settings.">AI Re-detect</button>
          <details style={{ position: 'relative' }}>
            <summary className="editor-tool-button editor-tool-button--subtle" aria-label="AI detection options">Options</summary>
            <button className="editor-tool-button editor-tool-button--danger" onClick={handleAutoConfigure}
                    disabled={editor.loading || editor.aiProgress?.show}
                    style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20, whiteSpace: 'nowrap' }}>Reset all fields with AI</button>
          </details>
          <button className="editor-tool-button editor-tool-button--link" onClick={() => setShowLinkModal(true)}
                  title="Create a patient link for this form.">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
            Generate Link
          </button>
          <button className="editor-tool-button editor-tool-button--save" onClick={handleSaveAll}
                  title="Save every field change in this form.">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h13l3 3v13H4z" /><path d="M8 4v6h8V4M8 20v-7h8v7" /></svg>
            Save All
          </button>
          <div className="editor-more-actions" ref={moreActionsRef}>
            <button className="editor-tool-button editor-tool-button--more" onClick={() => setMoreActionsOpen(open => !open)}
                    aria-haspopup="menu" aria-expanded={moreActionsOpen} aria-controls="editor-more-menu" title="More form actions">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /></svg>
              More
            </button>
            {moreActionsOpen && (
              <div className="editor-more-menu" id="editor-more-menu" role="menu" aria-label="More form actions">
                <button role="menuitem" onClick={() => { setMoreActionsOpen(false); handleRename(); }}>Rename template</button>
                <button role="menuitem" onClick={() => { setMoreActionsOpen(false); handleToggleType(); }}>Change form type</button>
                <div className="editor-more-menu-divider" role="separator" />
                <button role="menuitem" onClick={() => { setMoreActionsOpen(false); handleReset(); }}>Discard changes</button>
                <button role="menuitem" className="editor-more-menu-danger" onClick={() => { setMoreActionsOpen(false); handleDeleteTemplate(); }}>Delete template</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Progress */}
      {editor.aiProgress.show && (
        <div className="ai-progress-bar">
          <div className="ai-progress-inner" style={{ width: editor.aiProgress.progress + '%' }} />
          <div className="ai-progress-layers">
            {['AcroForm', 'Azure AI', 'Line Detection', 'GPT-4o Vision'].map((name, i) => (
              <span key={i} className={`ai-layer${editor.aiProgress.layer === i ? ' active' : ''}${editor.aiProgress.layer > i ? ' completed' : ''}`}>
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* MAIN CONTENT - 3 columns */}
      <div className="editor-content">
        {/* LEFT PANEL - Components */}
        <div className="editor-left-panel">
          <h3>Components</h3>
          <div className="component-list">
            {componentTypes.map(ct => (
              <div
                key={ct.type}
                className={`component-item${addFieldMode === ct.type ? ' active' : ''}`}
                onClick={() => toggleAddFieldMode(ct.type)}
              >
                <span className="component-icon">{ct.icon}</span>
                <span className="component-label">{ct.label}</span>
              </div>
            ))}
          </div>

          {addFieldMode && (
            <div className="add-field-hint">
              Click on the PDF to place a {addFieldMode} field.
              <br />Press ESC to cancel.
            </div>
          )}

          {/* Form-level settings */}
          <div className="form-settings">
            <h3>Form Settings</h3>
            <div className="form-group">
              <label>Global Font (Apply to All Fields)</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <select
                  className="form-control"
                  value={formLevelSettings.globalFontFamily}
                  onChange={(e) => handleFormSettingChange('globalFontFamily', e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Arial">Arial</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Tahoma">Tahoma</option>
                  <option value="Trebuchet MS">Trebuchet MS</option>
                  <option value="Comic Sans MS">Comic Sans MS</option>
                  <option value="Raleway">Raleway</option>
                </select>
                <select
                  className="form-control"
                  value={formLevelSettings.globalFontSize}
                  onChange={(e) => handleFormSettingChange('globalFontSize', e.target.value)}
                  style={{ width: '80px' }}
                >
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                  <option value="11">11</option>
                  <option value="12">12</option>
                  <option value="14">14</option>
                  <option value="16">16</option>
                  <option value="18">18</option>
                  <option value="20">20</option>
                  <option value="22">22</option>
                  <option value="24">24</option>
                  <option value="26">26</option>
                  <option value="28">28</option>
                  <option value="32">32</option>
                  <option value="36">36</option>
                  <option value="48">48</option>
                  <option value="72">72</option>
                </select>
              </div>
              <button
                className="btn btn-primary"
                onClick={handleApplyGlobalFont}
                disabled={!formLevelSettings.globalFontFamily && !formLevelSettings.globalFontSize}
                style={{ width: '100%' }}
              >
                Apply to All
              </button>
            </div>
            <div className="form-group">
              <label>
                Primary Field <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                className="form-control"
                value={formLevelSettings.primaryFieldName}
                onChange={(e) => handleFormSettingChange('primaryFieldName', e.target.value)}
                style={!formLevelSettings.primaryFieldName ? { borderColor: '#dc3545' } : undefined}
              >
                {/* Deliberately not a saveable choice. It used to fall back to the form
                    name, which produced PDFs called "...-Unknown.pdf" that staff could
                    not tell apart. Publishing already refuses without a primary field,
                    so offering a blank option only let the problem be discovered later. */}
                <option value="" disabled>-- Select a field --</option>
                {primaryFieldOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {!formLevelSettings.primaryFieldName && (
                <div style={{ color: '#dc3545', fontSize: 12, marginTop: 4 }}>
                  {primaryFieldOptions.length === 0
                    ? 'Add a text field to the form first — the primary field names the PDF and identifies the submission.'
                    : 'Required. This field names the submitted PDF and identifies the patient in My Submissions.'}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Checkbox Style</label>
              <select
                className="form-control"
                value={formLevelSettings.markStyle}
                onChange={(e) => handleFormSettingChange('markStyle', e.target.value)}
              >
                <option value="tick">Tick</option>
                <option value="cross">Cross</option>
                <option value="circle">Circle</option>
              </select>
            </div>
            <div className="form-group">
              <label>Radio Style</label>
              <select
                className="form-control"
                value={formLevelSettings.radioStyle}
                onChange={(e) => handleFormSettingChange('radioStyle', e.target.value)}
              >
                <option value="dot">Dot</option>
                <option value="tick">Tick</option>
                <option value="x">X</option>
              </select>
            </div>
            <div className="form-group">
              <label>Radio Border Color</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={formLevelSettings.radioBorderColor}
                  onChange={(e) => handleFormSettingChange('radioBorderColor', e.target.value)}
                  style={{ width: '40px', height: '30px', padding: '2px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  className="form-control"
                  value={formLevelSettings.radioBorderColor}
                  onChange={(e) => handleFormSettingChange('radioBorderColor', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                <span
                  onClick={() => handleFormSettingChange('showRadioBorder', !formLevelSettings.showRadioBorder)}
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    width: '36px',
                    height: '20px',
                    backgroundColor: formLevelSettings.showRadioBorder ? 'var(--company-primary-color, #273947)' : '#d1d5db',
                    borderRadius: '10px',
                    transition: 'background-color 0.2s',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '2px',
                    left: formLevelSettings.showRadioBorder ? '18px' : '2px',
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </span>
                Show Radio Border
              </label>
            </div>
            <div className="form-group">
              <label>Date Format</label>
              <select
                className="form-control"
                value={formLevelSettings.dateFormat}
                onChange={(e) => handleFormSettingChange('dateFormat', e.target.value)}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>
        </div>

        {/* CENTER - PDF Canvas */}
        <div className="editor-center-panel">
          <EditorCanvas
            canvasRef={canvasRef}
            overlayRef={overlayRef}
            containerRef={containerRef}
            pdfDoc={pdfDoc}
            currentPage={currentPage}
            scale={zoomLevel}
            pdfFields={editor.pdfFields}
            configuration={editor.configuration}
            selectedFieldIndex={editor.selectedFieldIndex}
            selectedFieldIndices={editor.selectedFieldIndices}
            addFieldMode={addFieldMode}
            onSelectField={editor.selectField}
            onToggleFieldSelection={editor.toggleFieldSelection}
            onClearSelection={editor.clearSelection}
            onUpdateFieldBounds={editor.updateFieldBounds}
            onCreateCustomField={handleCreateCustomField}
            onSetSelectedFieldIndices={editor.setSelectedFieldIndices}
            onSetSelectedFieldIndex={editor.setSelectedFieldIndex}
            viewportRef={viewportRef}
            onPageChange={setCurrentPage}
            onCanvasFocusRequest={focusEditorCanvas}
          />

          {/* Floating Toolbar */}
          <EditorFloatingToolbar
            visible={editor.selectedFieldIndex !== null}
            selectedFieldIndex={editor.selectedFieldIndex}
            pdfFields={editor.pdfFields}
            configuration={editor.configuration}
            overlayRef={overlayRef}
            onSaveField={handleSaveFieldFromToolbar}
            onDuplicateField={editor.duplicateField}
            onDeleteField={editor.deleteField}
          />
        </div>

        {/* RIGHT PANEL - Field Config + Fields List */}
        <div className="editor-right-panel">
          <EditorFieldConfig
            selectedFieldIndex={editor.selectedFieldIndex}
            pdfFields={editor.pdfFields}
            configuration={editor.configuration}
            onSaveField={handleSaveFieldFromPanel}
            onDeleteField={editor.deleteField}
            onCancelEdit={() => editor.clearSelection()}
          />

          <EditorFieldsList
            configuration={editor.configuration}
            pdfFields={editor.pdfFields}
            selectedFieldIndices={editor.selectedFieldIndices}
            onSelectField={editor.selectField}
            onDeleteFieldByName={editor.deleteFieldByName}
            onDeleteSelectedFields={editor.deleteSelectedFields}
            onDuplicateField={editor.duplicateField}
            currentPage={currentPage}
          />
        </div>
      </div>

      {/* Generate Link Modal */}
      <EditorLinkModal
        show={showLinkModal}
        formId={formId}
        onClose={() => setShowLinkModal(false)}
      />

      {/* Toast */}
      {toast && (
        <div className="toast-notification">{toast}</div>
      )}
    </div>
  );
}
